import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookMarked, Trash2, UploadCloud, Loader2, FileText } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config/api';
import { mediaUrl } from '../../lib/mediaUrl';

const API = `${API_BASE}/api`;
const CATEGORIES = ['Legislation', 'Regulator guidance', 'Internal policy', 'Standard', 'Other'];

export default function KnowledgeLibraryPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'Legislation' });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/admin/knowledge`, { headers }).then((r) => setDocs(r.data.documents || [])).catch(() => toast.error('Could not load library'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', form.title);
    fd.append('category', form.category);
    try {
      await axios.post(`${API}/admin/knowledge`, fd, { headers, timeout: 120000 });
      toast.success('Source added to the Knowledge Library');
      setForm({ ...form, title: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}" from the library? Existing courses keep their content.`)) return;
    try {
      await axios.delete(`${API}/admin/knowledge/${d.doc_id}`, { headers });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove source');
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="knowledge-library-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3"><BookMarked className="h-8 w-8 text-navigate-gold" />Knowledge Library</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">Upload approved sources such as Acts, FIC/FSCA guidance notes and internal policies. When you pick them while generating a Learning Unit, NAVI writes only from them and cites them per section.</p>

        <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end">
          <label className="text-sm text-zinc-400">Title (optional)
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. FIC Guidance Note 7" className="w-full h-10 mt-1 rounded-md bg-zinc-800 border border-zinc-700 text-white px-3" data-testid="knowledge-title-input" />
          </label>
          <label className="text-sm text-zinc-400">Category
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 mt-1 rounded-md bg-zinc-800 border border-zinc-700 text-white px-2" data-testid="knowledge-category-select">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-teal-600 hover:bg-teal-500 text-white h-10" data-testid="knowledge-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}Upload PDF / Word
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="knowledge-file-input" />
        </div>

        <div className="mt-6 space-y-2">
          {docs === null && <p className="text-zinc-500 text-sm">Loading…</p>}
          {docs?.length === 0 && <p className="text-zinc-500 text-sm" data-testid="knowledge-empty">No sources yet.</p>}
          {(docs || []).map((d) => (
            <div key={d.doc_id} className="flex items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3" data-testid={`knowledge-doc-${d.doc_id}`}>
              <FileText className="h-5 w-5 text-amber-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <a href={mediaUrl(d.file_url)} target="_blank" rel="noreferrer" className="text-white font-medium hover:text-teal-300 truncate block">{d.title}</a>
                <p className="text-xs text-zinc-500">{d.category} · {Math.round(d.chars / 1000)}k characters · added {new Date(d.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => remove(d)} className="p-2 text-zinc-500 hover:text-rose-300" data-testid={`knowledge-delete-${d.doc_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
